/// <reference types="office-js" />
import type { HostAdapter, HostKind } from './host-adapter'
import { WordAdapter } from './word-adapter'
import { BrowserAdapter } from './browser-adapter'

export interface OfficeReady {
  host: HostKind
  adapter: HostAdapter
}

function officeAvailable(): boolean {
  return typeof Office !== 'undefined' && typeof Office.onReady === 'function'
}

function detectHost(): HostKind {
  if (!officeAvailable() || !Office.context?.host) return 'browser'
  switch (Office.context.host) {
    case Office.HostType.Word:
      return 'word'
    case Office.HostType.Excel:
      return 'excel'
    case Office.HostType.PowerPoint:
      return 'powerpoint'
    case Office.HostType.Outlook:
      return 'outlook'
    default:
      return 'browser'
  }
}

function adapterFor(host: HostKind): HostAdapter {
  switch (host) {
    case 'word':
      return new WordAdapter()
    // Excel/PowerPoint/Outlook adapters land in later waves.
    default:
      return new BrowserAdapter()
  }
}

/** Await Office (if present) and return the matching HostAdapter; browser fallback otherwise. */
export async function initOffice(timeoutMs = 2000): Promise<OfficeReady> {
  if (!officeAvailable()) {
    return { host: 'browser', adapter: new BrowserAdapter() }
  }
  // Race onReady against a timeout so plain-browser dev never hangs when Office
  // is loaded from the CDN but no host is present.
  const ready = Office.onReady()
    .then(() => true)
    .catch(() => false)
  const timedOut = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs))
  const ok = await Promise.race([ready, timedOut])
  const host = ok ? detectHost() : 'browser'
  return { host, adapter: adapterFor(host) }
}
